import React, { useState, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { downloadPDF } from "../../utils/pdf";
import { formatINR, formatDate, today } from "../../utils/helpers";
import { CATEGORIES, PAYMENT_METHODS } from "../../utils/constants";
import { Plus, Trash2, X, AlertTriangle, Download } from "lucide-react";

export default function Expenses() {
  const { expenses, addExpense, deleteExpense, mode, currentUserId } = useApp();
  const displayExpenses = mode === "admin" ? expenses : expenses.filter(e => e.userId === currentUserId || e.addedBy === currentUserId);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ date: today(), amount: "", category: CATEGORIES[0].name, customCategory: "", description: "", paymentMethod: "UPI" });
  const [filter, setFilter] = useState("all");

  useEffect(() => { const h = () => setShowForm(false); window.addEventListener("closeModals", h); return () => window.removeEventListener("closeModals", h); }, []);

  let filtered = [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date));
  if (mode === "employee") filtered = filtered.filter(e => e.assignedTo === currentUserId);
  if (filter === "today") filtered = filtered.filter(e => e.date === today());
  if (filter === "week") { const w = new Date(); w.setDate(w.getDate() - 7); filtered = filtered.filter(e => new Date(e.date) >= w); }
  if (filter === "month") { const now = new Date(); filtered = filtered.filter(e => { const d = new Date(e.date); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }); }
  const totalFiltered = filtered.reduce((s, e) => s + e.amount, 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cat = form.category === "__other__" ? form.customCategory : form.category;
    if (form.category === "__other__" && !form.customCategory.trim()) return;
    const ok = addExpense({ ...form, category: cat });
    if (ok) { setShowForm(false); setForm({ date: today(), amount: "", category: CATEGORIES[0].name, customCategory: "", description: "", paymentMethod: "UPI" }); }
  };

  const handleDelete = (id) => {
    if (confirmDelete === id) { deleteExpense(id); setConfirmDelete(null); }
    else { setConfirmDelete(id); setTimeout(() => setConfirmDelete(null), 3000); }
  };

  
  const exportExpensesPDF = () => {
    downloadPDF("Expenses Report",
      ["Date", "Category", "Description", "Amount", "Payment"],
      expenses.map(e => [formatDate(e.date), e.category, e.description || "—", formatINR(e.amount), e.paymentMethod || "—"]),
      [
        { label: "Total Expenses", value: formatINR(displayExpenses.reduce((s, e) => s + Number(e.amount), 0)), color: "#EF4444" },
        { label: "Count", value: expenses.length + " entries" },
      ]
    );
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h3>Total: {formatINR(totalFiltered)}</h3><span className="text-muted">{filtered.length} expenses</span></div>
        <div className="header-actions">
          <div className="filter-tabs">
            {["all","today","week","month"].map(f => <button key={f} className={"tab"+(filter===f?" active":"")} onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</button>)}
          </div>
          <button className="btn-ghost" onClick={exportExpensesPDF} style={{display:"flex",alignItems:"center",gap:6}}><Download size={14}/> PDF</button>
        <button className="btn-gold" onClick={() => setShowForm(true)}><Plus size={16}/> Add Expense</button>
        </div>
      </div>
      <div className="table-card">
        <table>
          <thead><tr><th>Date</th><th>Description</th><th>Category</th><th>Amount</th><th>Method</th><th></th></tr></thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id}>
                <td className="mono">{formatDate(e.date)}</td>
                <td className="primary">{e.description || "No description"}</td>
                <td><span className="cat-badge">{e.category}</span></td>
                <td className="mono amount">{formatINR(e.amount)}</td>
                <td className="text-muted">{e.paymentMethod}</td>
                <td>
                  {confirmDelete === e.id ? (
                    <button className="btn-deny" style={{padding:"5px 12px",fontSize:11}} onClick={() => handleDelete(e.id)}>
                      <AlertTriangle size={13}/> Delete?
                    </button>
                  ) : (
                    <button className="icon-btn danger" onClick={() => handleDelete(e.id)}>
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan={6} className="empty-row">No expenses found.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Add Expense</h3><button className="icon-btn" onClick={() => setShowForm(false)}><X size={18}/></button></div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group"><label>Date</label><input type="date" value={form.date} max={today()} onChange={e => setForm({...form, date: e.target.value})} required/></div>
                <div className="form-group"><label>Amount (₹)</label><input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0" min="1" required/></div>
              </div>
              <div className="form-group"><label>Category</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value, customCategory: ""})}>
                  {CATEGORIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  <option value="__other__">Other (Custom)</option>
                </select>
              </div>
              {form.category === "__other__" && (
                <div className="form-group"><label>Custom Category Name</label>
                  <input type="text" value={form.customCategory} onChange={e => setForm({...form, customCategory: e.target.value})} placeholder="Enter category name" maxLength={50} required/>
                </div>
              )}
              <div className="form-group"><label>Description</label><input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What was this expense for?" maxLength={200}/></div>
              <div className="form-group"><label>Payment Method</label>
                <select value={form.paymentMethod} onChange={e => setForm({...form, paymentMethod: e.target.value})}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <button type="submit" className="btn-gold full">Add Expense</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
